/**
 * @packageDocumentation
 * Manages PixiJS asset bundles and font loading for both desktop and mobile builds, exposing helpers used by the preloader and main scene.
 */
/**
 * @module LoadAssets
 * @description Module for handling asset loading in the game
 */

import { Assets } from 'pixi.js';

/**
 * Checks if the current device is a mobile device
 * @returns {boolean} True if the device is mobile, false otherwise
 */
function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

let path = "pc"
if (isMobileDevice()) {
  path = "mobile"
}

/**
 * Loads initial preload assets required before game start
 * @param onProgress - Optional callback for tracking load progress
 * @returns Promise that resolves when all preload assets are loaded
 */
export async function loadPreloadAssets(onProgress?: (progress: number) => void): Promise<void> {
  Assets.addBundle('preloadAssets', {
    logo: "./assets/Images/logo_1.png",
    start_btn: './assets/Images/start_btn.png',
    preloader_anim_json: `./assets/spine/${path}/preloader_anim.json`,
    preloader_anim_atlas: `./assets/spine/${path}/preloader_anim.atlas`,
  });
  await Assets.loadBundle('preloadAssets');
  let supportedFontFormat = getBestSupportedFormat();
  await loadFont('American-Captain', './assets/Fonts/American-Captain.' + supportedFontFormat.ext)
  await loadFont('calibri', './assets/Fonts/calibri-regular.' + supportedFontFormat.ext);
  await loadFont('FranklinGothic-Demi', './assets/Fonts/FranklinGothic-Demi.' + supportedFontFormat.ext)
  await loadFont('Bangers-Regular', './assets/Fonts/Bangers-Regular.' + supportedFontFormat.ext)
}

/**
 * Loads all game assets required during gameplay
 * @param onProgress - Optional callback for tracking load progress
 * @returns Promise that resolves when all game assets are loaded
 */
export async function loadGameAssets(onProgress?: (progress: number) => void): Promise<void> {
  const bundleName = 'myGameAssets';
  function isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  let path = "pc"
  if (isMobileDevice()) {
    path = "mobile"
  }

  /**
   * Object containing all game asset paths 
   */
  const bundleAssets = {
    bg_preloader_wide: './assets/Images/bg_preloader_wide.jpg',
    Info_btn: './assets/Images/Info_btn.png',
    Outer_rectangle: './assets/Images/Outer_rectangle.png',
    bonus_reel_bg: './assets/Images/bonus_reel_bg.png',
    bonus_game_bg: "./assets/Images/bonus_game_bg.png",
    Bonuspage_bg: './assets/Images/Bonuspage_bg.jpg',
    reel_bg: './assets/Images/reel_bg.png',
    reelbg_1: './assets/Images/reelbg_1.png',
    collect_btn: './assets/Images/collect_btn.png',
    stop_btn: './assets/Images/stop_btn.png',
    popup: './assets/Images/popup.png',
    htp_popup: './assets/Images/htp_popup.png',
    Bet_but: './assets/Images/Bet_but.png',
    bg_game_wide: './assets/Images/bg_game_wide.jpg',
    fastplay_btn_with_clk: './assets/Images/fastplay_btn_with_clk.png',
    fastplay_btn_without_clk: './assets/Images/fastplay_btn_without_clk.png',
    glow: './assets/Images/glow.png',
    Plain_btn: './assets/Images/Plain_btn.png',
    Bet_minus_but: './assets/Images/Bet_minus_but.png',
    Bet_plus_but: './assets/Images/Bet_plus_but.png',
    arrow_right: './assets/Images/arrow_right.png',
    Yellow_superhero: './assets/Images/yellow_superhero.png',
    Yellow_civilian: './assets/Images/Yellow_civilian.png',
    Red_superhero: './assets/Images/Red_superhero.png',
    Red_civilian: './assets/Images/Red_civilian.png',
    Purple_superhero: './assets/Images/Purple_superhero.png',
    Purple_civilian: './assets/Images/Purple_civilian.png',
    Green_superhero: './assets/Images/Green_superhero.png',
    Green_civilian: './assets/Images/Green_civilian.png',
    Blue_superhero: './assets/Images/Blue_superhero.png',
    Blue_civilian: './assets/Images/Blue_civilian.png',
    silver: './assets/Images/silver.png',
    bronze: './assets/Images/bronze.png',
    halo: './assets/Images/halo.png',
    gold: './assets/Images/gold.png',
    frameInner: "./assets/Images/frame_inner.png",
    frameOuter: "./assets/Images/frame_outer.png",
    but_autocount: './assets/Images/but_autocount.png',
    but_autocount_shape: './assets/Images/but_autocount_shape.png',
    Spin_but: './assets/Images/Spin_but.png',
    bg_game_wide_top: './assets/Images/bg_game_wide_top.png',
    Total_win_but: './assets/Images/Total_win_but.png',
    How_to_play_but: './assets/Images/How_to_play_but.png',
    prizechart_popup: './assets/Images/prizechart_popup.png',
    Prize_chart_but: './assets/Images/Prize_chart_but.png',
    startPage_logo: './assets/Images/logo_1.png',
    startPage_card: './assets/Images/landing_bg_box.png',
    startPage_badge: './assets/Images/landing_bg_badge.png',
    startPage_pyramid: './assets/Images/landing_bg_pyramid.png',
    startPage_popup: './assets/Images/landing_page_popup.png',
    Autoplay_but: './assets/Images/Autoplay_but.png',
    stop_autoplay_but: './assets/Images/stop_autoplay_but.png',

    winvalue_bg: './assets/Images/winvalue_bg.png',
    landing_bg_box_big: './assets/Images/landing_bg_box_big.png',
    but_start_autoplay: './assets/Images/but_start_autoplay.png',
    autoplay_bg: './assets/Images/autoplay_bg.png',
    lp_circle: './assets/Images/lp_circle.png',
    // cash_bg :'./assets/Images/cash_bg.png', 
    // boom :'./assets/Images/boom.png',  

    bonus_reel_bg_gold: "assets/Images/bonus_reel_bg_gold.png",
    bonus_reel_bg_stone: "assets/Images/bonus_reel_bg_stone.png",
    bonus_reel_bg_brick: "assets/Images/bonus_reel_bg_brick.png",
    bonus_reel_bg_wood: "assets/Images/bonus_reel_bg_wood.png",
    bonus_reel_bg_mud: "assets/Images/bonus_reel_bg_mud.png",

    close_btn: './assets/Images/close_btn.png',
    cloud_1: './assets/Images/cloud_1.png',
    cloud_2: './assets/Images/cloud_2.png',
    cloud_3: './assets/Images/cloud_3.png',
    // cloud_4:'./assets/Images/cloud_4.png', 
    // cloud_5:'./assets/Images/cloud_5.png', 
    // cloud_6:'./assets/Images/cloud_6.png', 
    // cloud_7:'./assets/Images/cloud_7.png', 
    // cloud_8:'./assets/Images/cloud_8.png', 
    // cloud_9:'./assets/Images/cloud_9.png', 
    // cloud_10:'./assets/Images/cloud_10.png', 
    // cloud_11:'./assets/Images/cloud_11.png',   

    backcard: './assets/Images/card.png',
    civilian_wild_card: './assets/Images/civilian_wild_card.png',
    hero_wild_card: './assets/Images/hero_wild_card.png',

    // Spine animations
    logo_anim_json: `./assets/spine/${path}/logo_anim.json`,
    logo_anim_atlas: `./assets/spine/${path}/logo_anim.atlas`,

    Total_win_popup_anim_json: `./assets/spine/${path}/Total_win_popup_anim.json`,
    Total_win_popup_anim_atlas: `./assets/spine/${path}/Total_win_popup_anim.atlas`,


    transition_anim_json: `./assets/spine/${path}/transition_anim.json`,
    transition_anim_atlas: `./assets/spine/${path}/transition_anim.atlas`,

    start_btn_anim_json: `./assets/spine/${path}/start_btn_anim.json`,
    start_btn_anim_atlas: `./assets/spine/${path}/start_btn_anim.atlas`,

    celebration_anim_json: `./assets/spine/${path}/celebration_anim.json`,
    celebration_anim_atlas: `./assets/spine/${path}/celebration_anim.atlas`,

    Preloader_anim_json: `./assets/spine/${path}/preloader_anim.json`,
    Preloader_anim_atlas: `./assets/spine/${path}/preloader_anim.atlas`,


    bms_win_anim_json: `./assets/spine/${path}/bms_win_anim.json`,
    bms_win_anim_atlas: `./assets/spine/${path}/bms_win_anim.atlas`,

    Blue_civilian_json: `./assets/spine/${path}/Blue_civilian.json`,
    Blue_civilian_atlas: `./assets/spine/${path}/Blue_civilian.atlas`,

    Blue_superhero_json: `./assets/spine/${path}/Blue_superhero.json`,
    Blue_superhero_atlas: `./assets/spine/${path}/Blue_superhero.atlas`,

    Green_civilian_json: `./assets/spine/${path}/Green_civilian.json`,
    Green_civilian_atlas: `./assets/spine/${path}/Green_civilian.atlas`,

    Green_superhero_json: `./assets/spine/${path}/Green_superhero.json`,
    Green_superhero_atlas: `./assets/spine/${path}/Green_superhero.atlas`,

    Purple_civilian_json: `./assets/spine/${path}/Purple_civilian.json`,
    Purple_civilian_atlas: `./assets/spine/${path}/Purple_civilian.atlas`,

    Purple_superhero_json: `./assets/spine/${path}/Purple_superhero.json`,
    Purple_superhero_atlas: `./assets/spine/${path}/Purple_superhero.atlas`,

    Red_civilian_json: `./assets/spine/${path}/Red_civilian.json`,
    Red_civilian_atlas: `./assets/spine/${path}/Red_civilian.atlas`,

    Red_superhero_json: `./assets/spine/${path}/Red_superhero.json`,
    Red_superhero_atlas: `./assets/spine/${path}/Red_superhero.atlas`,

    Yellow_civilian_json: `./assets/spine/${path}/Yellow_civilian.json`,
    Yellow_civilian_atlas: `./assets/spine/${path}/Yellow_civilian.atlas`,

    Yellow_superhero_json: `./assets/spine/${path}/yellow_superhero.json`,
    Yellow_superhero_atlas: `./assets/spine/${path}/yellow_superhero.atlas`,
    /*
        bronze_frame_anim_json: `./assets/spine/${path}/bronze_frame_anim.json`,
        bronze_frame_anim_atlas: `./assets/spine/${path}/bronze_frame_anim.atlas`,
    
        Silver_frame_anim_json: `./assets/spine/${path}/Silver_frame_anim.json`,
        Silver_frame_anim_atlas: `./assets/spine/${path}/Silver_frame_anim.atlas`,
    
        Gold_frame_anim_json: `./assets/spine/${path}/Gold_frame_anim.json`,
        Gold_frame_anim_atlas: `./assets/spine/${path}/Gold_frame_anim.atlas`,
    
        Hologram_frame_anim_json: `./assets/spine/${path}/Hologram_frame_anim.json`,
        Hologram_frame_anim_atlas: `./assets/spine/${path}/Hologram_frame_anim.atlas`,
    */
    bronze_frame_anim_json: `./assets/spine/${path}/card_frame/bronze/skeleton.json`,
    bronze_frame_anim_atlas: `./assets/spine/${path}/card_frame/bronze/cards_bronze.atlas`,

    Silver_frame_anim_json: `./assets/spine/${path}/card_frame/silver/skeleton.json`,
    Silver_frame_anim_atlas: `./assets/spine/${path}/card_frame/silver/cards_silver.atlas`,

    Gold_frame_anim_json: `./assets/spine/${path}/card_frame/gold/skeleton.json`,
    Gold_frame_anim_atlas: `./assets/spine/${path}/card_frame/gold/cards_gold.atlas`,

    Hologram_frame_anim_json: `./assets/spine/${path}/card_frame/hologram/skeleton.json`,
    Hologram_frame_anim_atlas: `./assets/spine/${path}/card_frame/hologram/cards_hologram.atlas`,

    robo_anim_json: `./assets/spine/${path}/robo_anim.json`,
    robo_anim_atlas: `./assets/spine/${path}/robo_anim.atlas`,

    bonus_transition_json: `./assets/spine/${path}/bonus_transition.json`,
    bonus_transition_atlas: `./assets/spine/${path}/bonus_transition.atlas`,

    box_shatter_anim_json: `./assets/spine/${path}/box_shatter_anim.json`,
    box_shatter_anim_atlas: `./assets/spine/${path}/box_shatter_anim.atlas`,

    bonus_game_blocks_anim_json: `./assets/spine/${path}/bonusGame_blocks/skeleton.json`,
    bonus_game_blocks_anim_atlas: `./assets/spine/${path}/bonusGame_blocks/bonusGame_blocks.atlas`,

    power_up_or_cash_anim_json: `./assets/spine/${path}/power_up_or_cash/skeleton.json`,
    power_up_or_cash_anim_atlas: `./assets/spine/${path}/power_up_or_cash/WinBlock.atlas`,

    beam_anim_anim_json: `./assets/spine/${path}/beam_anim_anim.json`,
    beam_anim_anim_atlas: `./assets/spine/${path}/beam_anim_anim.atlas`,

    laser_burst_anim_json: `./assets/spine/${path}/laser_burst_anim.json`,
    laser_burst_anim_atlas: `./assets/spine/${path}/laser_burst_anim.atlas`,

    powerup_anim_json: `./assets/spine/${path}/powerup_anim.json`,
    powerup_anim_atlas: `./assets/spine/${path}/powerup_anim.atlas`,

  };

  Assets.addBundle(bundleName, bundleAssets);
  const assetKeys = Object.keys(bundleAssets);
  const totalAssets = assetKeys.length;
  let loadedAssets = 0;

  const loadPromises = assetKeys.map(async (key) => {
    await Assets.load(key);
    loadedAssets++;
    if (onProgress) {
      onProgress(loadedAssets / totalAssets);
    }
  });

  await Promise.all(loadPromises);
}

/**
 * Loads a custom font using FontFace API
 * @param name - Name of the font to load
 * @param url - URL of the font file
 * @returns Promise that resolves when font is loaded
 */
export function loadFont(name: string, url: string) {
  const font = new FontFace(name, `url(${url})`);
  return font.load().then(function (loadedFont) {
    document.fonts.add(loadedFont);
    console.log(`${name} font loaded`);
  }).catch(function (error) {
    console.error(`Failed to load ${name} font`, error);
  });
}

/**
 * Interface for font format information
 */
type FontFormat = {
  extension: string;
  mimeType: string;
  ext: string;
};

/**
 * Detects the best supported font format for the current browser
 * @returns FontFormat object containing format information
 */
export function getBestSupportedFormat(): FontFormat {
  const formats: FontFormat[] = [
    { extension: "woff", mimeType: "font/woff", ext: "woff" },
    { extension: "otf", mimeType: "font/otf", ext: "opentype" },
    { extension: "ttf", mimeType: "font/ttf", ext: "truetype" },
    {
      extension: "eot",
      mimeType: "application/vnd.ms-fontobject",
      ext: "opentype",
    },
    { extension: "svg", mimeType: "image/svg+xml", ext: "svg" },
  ];

  const div = document.createElement("div");

  for (let i = 0; i < formats.length; i++) {
    try {
      div.style.font = `url(data:${formats[i].mimeType};base64,AAEAAAAKAIAAAwQgRkZUT0eRhwAAH3UAAAAHAfD1WIDFSA)`;
      if (div.style.font) {
        return formats[i];
      }
    } catch (e) {
      continue;
    }
  }

  return formats[0];
}

